import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';

// ── Data ──────────────────────────────────────────────────────────────────────

const EMP_STATUS = [
  { value:'employed_full_time',  label:'Employed (Full-Time)',  icon:'💼' },
  { value:'employed_part_time',  label:'Employed (Part-Time)',  icon:'🕐' },
  { value:'unemployed',          label:'Unemployed / Looking',  icon:'🔍' },
  { value:'student',             label:'Student',               icon:'🎓' },
  { value:'recent_graduate',     label:'Recent Graduate',       icon:'🏆' },
  { value:'self_employed',       label:'Self-Employed',         icon:'🚀' },
];

const DEGREES = [
  { value:'none',        label:'No Degree',       icon:'📄' },
  { value:'high_school', label:'High School / GED', icon:'🏫' },
  { value:'certificate', label:'Certificate',     icon:'📜' },
  { value:'associate',   label:"Associate's",    icon:'📘' },
  { value:'bachelor',    label:"Bachelor's",     icon:'🎓' },
  { value:'master',      label:"Master's",       icon:'📚' },
  { value:'doctorate',   label:'Doctorate / PhD', icon:'🔬' },
];

const SECTORS = [
  'Healthcare', 'Government', 'Education', 'Business & Finance',
  'Technology & AI', 'Workforce & Labor', 'Nonprofit', 'Legal',
  'Engineering', 'Creative & Media', 'Other',
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function ProgressBar({ step, total }) {
  return (
    <div style={{ marginBottom:36 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, alignItems:'center' }}>
        <span style={{ fontSize:12, color:'rgba(245,240,232,.55)', fontWeight:600, letterSpacing:'.06em', textTransform:'uppercase' }}>Step {step} of {total}</span>
        <span style={{ fontSize:12, color:'rgba(245,240,232,.4)' }}>{Math.round((step / total) * 100)}% complete</span>
      </div>
      <div style={{ height:4, background:'rgba(245,240,232,.15)', borderRadius:2, overflow:'hidden' }}>
        <div style={{
          width:`${(step / total) * 100}%`, height:'100%',
          background:'linear-gradient(90deg, #1B6B72, #C9A84C)',
          borderRadius:2, transition:'width .5s cubic-bezier(.4,0,.2,1)',
        }} />
      </div>
    </div>
  );
}

function SelectCard({ value, label, icon, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`card-${value}`}
      style={{
        width:'100%', padding:'14px 16px', borderRadius:12, cursor:'pointer',
        border: selected ? '2px solid #C9A84C' : '1.5px solid rgba(245,240,232,.15)',
        background: selected ? 'rgba(201,168,76,.15)' : 'rgba(245,240,232,.04)',
        color: selected ? '#F5F0E8' : 'rgba(245,240,232,.75)',
        display:'flex', alignItems:'center', gap:12, textAlign:'left',
        fontFamily:'Inter,sans-serif', transition:'all .15s',
        boxShadow: selected ? '0 0 0 1px rgba(201,168,76,.3)' : 'none',
      }}
    >
      <span style={{ fontSize:22, flexShrink:0 }}>{icon}</span>
      <span style={{ fontSize:14, fontWeight: selected ? 700 : 500 }}>{label}</span>
      {selected && (
        <span style={{ marginLeft:'auto', width:18, height:18, borderRadius:'50%', background:'#C9A84C', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:10, color:'#0D2137', fontWeight:900 }}>✓</span>
      )}
    </button>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FirstBriefcasePage() {
  const navigate = useNavigate();
  const { token } = useJBAuth();

  const [step, setStep]             = useState(1);
  const [empStatus, setEmpStatus]   = useState('');
  const [degree, setDegree]         = useState('');
  const [openToSchool, setOTS]      = useState(null); // true | false | null
  const [sectors, setSectors]       = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  function toggleSector(s) {
    setSectors(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  }

  function canAdvance() {
    if (step === 1) return !!empStatus;
    if (step === 2) return !!degree;
    if (step === 3) return openToSchool !== null;
    if (step === 4) return sectors.length > 0;
    return true;
  }

  async function handleSubmit() {
    if (!canAdvance()) return;
    setSubmitting(true);
    setError('');
    try {
      await jbApi.firstBriefcaseIntake({
        employment_status:   empStatus,
        highest_degree:      degree,
        open_to_school:      openToSchool,
        sectors_of_interest: sectors,
      }, token || undefined);

      // Redirect to career-hub filtered by selected sectors
      const params = new URLSearchParams({ sectors: sectors.join(',') });
      navigate(`/career-hub?${params.toString()}`);
    } catch (err) {
      setError(err.detail || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="jb-page" style={{ minHeight:'100vh', background:'linear-gradient(135deg, #0D2137 0%, #0a1b2e 60%, #061220 100%)' }} data-testid="first-briefcase-page">

      {/* Top bar */}
      <div style={{ padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', maxWidth:740, margin:'0 auto' }}>
        <Link to="/career-hub" style={{ fontSize:12, color:'rgba(245,240,232,.5)', textDecoration:'none', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase' }}>
          ← Career Hub
        </Link>
        {step > 1 && (
          <button
            onClick={() => setStep(s => s - 1)}
            style={{ background:'transparent', border:'none', color:'rgba(245,240,232,.5)', fontSize:12, cursor:'pointer', fontFamily:'Inter,sans-serif' }}
            data-testid="back-btn"
          >
            ← Back
          </button>
        )}
      </div>

      {/* Card */}
      <div style={{ maxWidth:600, margin:'0 auto', padding:'0 24px 60px' }}>
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <div style={{ fontSize:11, color:'#C9A84C', fontWeight:800, letterSpacing:'.12em', textTransform:'uppercase', marginBottom:10 }}>
            Your First Briefcase
          </div>
          <h1 style={{ fontFamily:'Inter,sans-serif', fontSize:28, fontWeight:900, color:'#F5F0E8', margin:0, lineHeight:1.25 }}>
            {step === 1 && "What's your current employment status?"}
            {step === 2 && "What's your highest level of education?"}
            {step === 3 && "Are you open to going back to school?"}
            {step === 4 && "Which sectors interest you most?"}
          </h1>
          <p style={{ fontSize:14, color:'rgba(245,240,232,.5)', marginTop:10 }}>
            {step === 1 && "Help us match you with the right opportunities."}
            {step === 2 && "We use this to surface relevant roles and programs."}
            {step === 3 && "We partner with institutions that can help you level up."}
            {step === 4 && "Select all that apply. We'll personalise your job board."}
          </p>
        </div>

        <ProgressBar step={step} total={4} />

        {error && (
          <div style={{ background:'rgba(220,38,38,.15)', border:'1px solid rgba(220,38,38,.3)', borderRadius:10, padding:'12px 16px', color:'#fca5a5', fontSize:13, marginBottom:20 }} data-testid="fb-error">
            {error}
          </div>
        )}

        {/* ── Step 1: Employment Status ── */}
        {step === 1 && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }} data-testid="step-1">
            {EMP_STATUS.map(opt => (
              <SelectCard
                key={opt.value}
                {...opt}
                selected={empStatus === opt.value}
                onClick={() => setEmpStatus(opt.value)}
              />
            ))}
          </div>
        )}

        {/* ── Step 2: Highest Degree ── */}
        {step === 2 && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }} data-testid="step-2">
            {DEGREES.map(opt => (
              <SelectCard
                key={opt.value}
                {...opt}
                selected={degree === opt.value}
                onClick={() => setDegree(opt.value)}
              />
            ))}
          </div>
        )}

        {/* ── Step 3: Open to School ── */}
        {step === 3 && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }} data-testid="step-3">
            <div style={{ background:'rgba(27,107,114,.1)', border:'1px solid rgba(27,107,114,.25)', borderRadius:12, padding:'14px 18px', fontSize:13, color:'rgba(245,240,232,.7)', lineHeight:1.6 }}>
              We partner with accredited institutions and certification programs to help you advance your career. Saying "Yes" lets us surface relevant educational pathways alongside job listings.
            </div>

            {[
              { val:true,  label:'Yes — show me education options',  sub:"I'm open to courses, certifications, or degree programs", icon:'📖' },
              { val:false, label:'No — jobs only please',           sub:'I want to focus on employment opportunities only',         icon:'💼' },
            ].map(opt => (
              <button
                key={String(opt.val)}
                type="button"
                onClick={() => setOTS(opt.val)}
                data-testid={`school-${opt.val ? 'yes' : 'no'}`}
                style={{
                  width:'100%', padding:'18px 20px', borderRadius:14, cursor:'pointer',
                  border: openToSchool === opt.val ? '2px solid #C9A84C' : '1.5px solid rgba(245,240,232,.15)',
                  background: openToSchool === opt.val ? 'rgba(201,168,76,.15)' : 'rgba(245,240,232,.04)',
                  color:'#F5F0E8', display:'flex', alignItems:'flex-start', gap:14,
                  textAlign:'left', fontFamily:'Inter,sans-serif', transition:'all .15s',
                  boxShadow: openToSchool === opt.val ? '0 0 0 1px rgba(201,168,76,.3)' : 'none',
                }}
              >
                <span style={{ fontSize:26, flexShrink:0, marginTop:2 }}>{opt.icon}</span>
                <div>
                  <div style={{ fontSize:15, fontWeight: openToSchool === opt.val ? 800 : 600, marginBottom:4 }}>{opt.label}</div>
                  <div style={{ fontSize:12, color:'rgba(245,240,232,.5)', fontWeight:400 }}>{opt.sub}</div>
                </div>
                {openToSchool === opt.val && (
                  <span style={{ marginLeft:'auto', marginTop:4, width:20, height:20, borderRadius:'50%', background:'#C9A84C', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11, color:'#0D2137', fontWeight:900 }}>✓</span>
                )}
              </button>
            ))}

            {/* Graduation cap animation — shown when user selects Yes */}
            {openToSchool === true && (
              <div
                data-testid="enrollforge-nudge"
                style={{
                  animation: 'fadeSlideIn .45s cubic-bezier(.2,.8,.3,1) both',
                  background: 'rgba(27,107,114,.18)',
                  border: '1px solid rgba(27,107,114,.4)',
                  borderRadius: 14,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                }}
              >
                <span style={{
                  fontSize: 32,
                  animation: 'capBounce .6s cubic-bezier(.36,.07,.19,.97) .1s both',
                  display: 'inline-block',
                  transformOrigin: 'center bottom',
                }}>🎓</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1B6B72', marginBottom: 3 }}>
                    Great!
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(245,240,232,.75)', lineHeight: 1.5 }}>
                    We'll connect you with education pathways that fit your goals.
                  </div>
                </div>
              </div>
            )}

            <style>{`
              @keyframes fadeSlideIn {
                from { opacity: 0; transform: translateY(10px); }
                to   { opacity: 1; transform: translateY(0); }
              }
              @keyframes capBounce {
                0%   { transform: rotate(-15deg) scale(.7); }
                50%  { transform: rotate(8deg)  scale(1.15); }
                75%  { transform: rotate(-4deg) scale(.97); }
                100% { transform: rotate(0deg)  scale(1); }
              }
            `}</style>
          </div>
        )}

        {/* ── Step 4: Sectors ── */}
        {step === 4 && (
          <div data-testid="step-4">
            <div style={{ display:'flex', flexWrap:'wrap', gap:10 }} data-testid="sector-chips">
              {SECTORS.map(s => {
                const active = sectors.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSector(s)}
                    data-testid={`sector-${s.replace(/[\s&/]/g, '-').toLowerCase()}`}
                    style={{
                      padding:'10px 20px', borderRadius:999, fontSize:13, fontWeight:600,
                      cursor:'pointer', fontFamily:'Inter,sans-serif', transition:'all .15s',
                      border: active ? '2px solid #C9A84C' : '1.5px solid rgba(245,240,232,.2)',
                      background: active ? 'rgba(201,168,76,.2)' : 'rgba(245,240,232,.06)',
                      color: active ? '#F5F0E8' : 'rgba(245,240,232,.65)',
                      boxShadow: active ? '0 0 0 1px rgba(201,168,76,.25)' : 'none',
                    }}
                  >
                    {active ? '✓ ' : ''}{s}
                  </button>
                );
              })}
            </div>
            {sectors.length > 0 && (
              <div style={{ fontSize:12, color:'rgba(201,168,76,.8)', marginTop:14, fontWeight:600 }}>
                {sectors.length} sector{sectors.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div style={{ marginTop:32, display:'flex', justifyContent:'center' }}>
          {step < 4 ? (
            <button
              type="button"
              disabled={!canAdvance()}
              onClick={() => setStep(s => s + 1)}
              data-testid="next-btn"
              style={{
                padding:'14px 40px', borderRadius:12, fontSize:15, fontWeight:800,
                cursor: canAdvance() ? 'pointer' : 'not-allowed',
                background: canAdvance() ? 'linear-gradient(135deg, #C9A84C, #b8923e)' : 'rgba(245,240,232,.12)',
                color: canAdvance() ? '#0D2137' : 'rgba(245,240,232,.3)',
                border:'none', fontFamily:'Inter,sans-serif', transition:'all .2s',
                boxShadow: canAdvance() ? '0 4px 16px rgba(201,168,76,.35)' : 'none',
                minWidth:180,
              }}
            >
              Continue →
            </button>
          ) : (
            <button
              type="button"
              disabled={!canAdvance() || submitting}
              onClick={handleSubmit}
              data-testid="submit-btn"
              style={{
                padding:'14px 40px', borderRadius:12, fontSize:15, fontWeight:800,
                cursor: (canAdvance() && !submitting) ? 'pointer' : 'not-allowed',
                background: (canAdvance() && !submitting) ? 'linear-gradient(135deg, #1B6B72, #134f55)' : 'rgba(245,240,232,.12)',
                color: (canAdvance() && !submitting) ? '#F5F0E8' : 'rgba(245,240,232,.3)',
                border:'none', fontFamily:'Inter,sans-serif', transition:'all .2s',
                boxShadow: (canAdvance() && !submitting) ? '0 4px 16px rgba(27,107,114,.4)' : 'none',
                minWidth:220,
              }}
            >
              {submitting ? 'Setting up your hub…' : 'Show Me Opportunities →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
